import { memo, useCallback, useState, type MouseEvent } from 'react'
import { Handle, Position, type NodeProps, useReactFlow } from '@xyflow/react'
import { Button } from '@/ui/button'
import { MaterialIcon } from '@/ui/material-icon'
import type { IntegrationNode as IntegrationNodeType } from '../../types/canvas.types'
import { areNodePropsEqual } from '../nodeMemo.util'
import styles from './IntegrationNode.module.scss'
import { TruncatedSecondaryTitle } from '../TruncatedSecondaryTitle'
import NodeValidationBadge from '../../validation/NodeValidationBadge'
import { useAppDispatch } from '@/app/reduxHooks'
import { openOverlay } from '@/features/adp/state/overlaySlice'

const IntegrationNode = memo(({ id, data }: NodeProps<IntegrationNodeType>) => {
  const [expanded, setExpanded] = useState(true)
  const { setNodes, setEdges } = useReactFlow()
  const locked = Boolean(data.locked)
  const dispatch = useAppDispatch()

  const handleSettings = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      // Route by abbreviation copied onto node data at drop time. Falls back to
      // Join for legacy nodes saved before abbreviation was carried — those
      // nodes were necessarily Join (the only integration wired at the time).
      const abbreviation = typeof data.abbreviation === 'string' ? data.abbreviation : 'JR'
      const transformationId = typeof data.originalId === 'number' ? data.originalId : null
      if (abbreviation === 'UN') {
        dispatch(
          openOverlay({
            type: 'union-integration-sheet',
            data: { nodeId: id, transformationId },
          }),
        )
        return
      }
      dispatch(
        openOverlay({
          type: 'join-integration-sheet',
          data: { nodeId: id, transformationId },
        }),
      )
    },
    [dispatch, id, data.abbreviation, data.originalId],
  )

  const handleToggleExpand = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    setExpanded(v => !v)
  }, [])

  const handleLock = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes =>
        nodes.map(node => {
          if (node.id !== id) return node
          const nodeData = node.data as IntegrationNodeType['data']
          const nextLocked = !Boolean(nodeData.locked)
          return {
            ...node,
            draggable: !nextLocked,
            data: { ...node.data, locked: nextLocked },
          }
        })
      )
    },
    [id, setNodes]
  )

  const handleDuplicate = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes => {
        const self = nodes.find(node => node.id === id)
        if (!self) return nodes
        const clone = {
          ...self,
          id: `${self.id}-copy-${Date.now()}`,
          position: { x: self.position.x + 28, y: self.position.y + 28 },
          draggable: true,
          data: { ...self.data, locked: false },
        }
        return nodes.concat(clone)
      })
    },
    [id, setNodes]
  )

  const handleDelete = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation()
      setNodes(nodes => nodes.filter(node => node.id !== id))
      setEdges(edges => edges.filter(edge => edge.source !== id && edge.target !== id))
    },
    [id, setEdges, setNodes]
  )

  const titlesBlock = (
    <div className={styles.titles}>
      <div className={styles.title}>{data.nodeTitle}</div>
      {data.nodeSecondaryTitle && (
        <TruncatedSecondaryTitle text={data.nodeSecondaryTitle} className={styles.secondaryTitle} />
      )}
    </div>
  )

  return (
    <div className={styles.node} data-validation={data.validation?.severity}>
      <NodeValidationBadge nodeId={id} validation={data.validation} />
      <Handle
        type="target"
        position={Position.Left}
        className={styles.handle}
        isConnectable={!locked}
      />
      <div className={styles.cardInner}>
        {/* ── Header (shared between expanded & collapsed) ── */}
        <div className={`${styles.header} ${!expanded ? styles.headerCollapsed : ''}`}>
          <div className={styles.headerLeft}>
            <span className={styles.headerTitle}>Integration</span>
            {expanded && (
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.headerIconBtn} nodrag`}
                aria-label="Settings"
                onPointerDown={e => e.stopPropagation()}
                onClick={handleSettings}
              >
                <MaterialIcon name="settings" size={16} />
              </Button>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            className={`${styles.headerIconBtn} nodrag`}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            onPointerDown={e => e.stopPropagation()}
            onClick={handleToggleExpand}
          >
            <MaterialIcon name="unfold_more" size={18} />
          </Button>
        </div>

        {expanded ? (
          <>
            {/* ── Body ── */}
            <div className={styles.body}>
              {data.image && (
                <div className={styles.imageWrapper}>
                  <img src={data.image} alt={data.nodeTitle} className={styles.image} />
                </div>
              )}
              {titlesBlock}
            </div>

            {/* ── Footer ── */}
            <div className={styles.footer}>
              <Button
                variant="ghost"
                size="icon"
                className={`${styles.footerBtn} nodrag`}
                aria-label={locked ? 'Unlock node' : 'Lock node'}
                aria-pressed={locked}
                onPointerDown={e => e.stopPropagation()}
                onClick={handleLock}
              >
                <MaterialIcon name={locked ? 'lock' : 'lock_open'} size={18} />
              </Button>
              <div className={styles.footerRight}>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} nodrag`}
                  aria-label="Duplicate"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDuplicate}
                >
                  <MaterialIcon name="content_copy" size={18} />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className={`${styles.footerBtn} ${styles.footerDelete} nodrag`}
                  aria-label="Delete"
                  onPointerDown={e => e.stopPropagation()}
                  onClick={handleDelete}
                >
                  <MaterialIcon name="delete" size={18} />
                </Button>
              </div>
            </div>
          </>
        ) : (
          /* ── Collapsed row ── */
          <div className={styles.collapsedRow}>
            {data.image && (
              <div className={styles.logoWellSm}>
                <img src={data.image} alt={data.nodeTitle} className={styles.logoImgSm} />
              </div>
            )}
            {titlesBlock}
          </div>
        )}
      </div>
      <Handle
        type="source"
        position={Position.Right}
        className={styles.handle}
        isConnectable={!locked}
      />
    </div>
  )
}, areNodePropsEqual)

IntegrationNode.displayName = 'IntegrationNode'

export { IntegrationNode }
